import { useContext, useEffect, useState } from 'react';
import { useField, useFormikContext } from 'formik';
import Select from 'react-select';
import { handleKeyPress } from '@rabbit/elements/shared-components';
import { SearchRetailers } from '@rabbit/search/cherchons';
import { InputTypeSelectSettingsShape } from '@rabbit/elements/shared-types';
import {
  DTBusiness_User_Invite,
  DTRetailer_Public,
  FBD_Business_User_Invite,
  FBD_Retailer_Public,
  PersonaIdTypeSplitter,
  PersonaTypeSingleLetter,
} from '@rabbit/data/types';
import { useDebounce } from '../../../utils/hooks/useDebounce';
import { generateSelectStyles } from '../../../utils/consts';
import Bus from '@rabbit/bus';
import { AppContext } from '@rabbit/app-context';
import { useTranslation } from 'react-i18next';
import { UserContext } from '@rabbit/sage/context/UserContext';
import { nestApiGetMainGroup } from '@rabbit/bizproc/core';

export interface InputAutoCompleteLocationRetailersProps {
  name: string;
  settings: InputTypeSelectSettingsShape;
  onChange?: (data: any) => void;
}

export function InputAutoCompleteLocationRetailers({
  name,
  settings,
  onChange: defaultOnChange,
}: InputAutoCompleteLocationRetailersProps) {
  const { setFieldValue } = useFormikContext();
  const {
    placeholder,
    isMulti = false,
    isClearable = true,
    disabled = false,
    value,
    ...props
  } = settings;
  const [field, meta] = useField(name);

  const [options, setOptions] = useState([]);
  const [inputText, setInputText] = useState('');
  const debouncedInput = useDebounce(inputText, 500);
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  const { config } = useContext(AppContext);
  const { t } = useTranslation();
  const { activeTenantInfo, retailerPersonaId } = useContext(UserContext) ?? {};

  let errorField = false;
  if (meta?.error) errorField = true;

  async function onChangeDefault(option: any) {
    if (isMulti) {
      await setFieldValue(
        name,
        option ? option.map((item: { value: any }) => item.value) : []
      );
    } else {
      await setFieldValue(name, option?.value ? option.value : '');
    }
    defaultOnChange && defaultOnChange(option);
  }

  const getValue = () => {
    if (options) {
      return isMulti
        ? options.filter(
            (option: { value: any }) => field.value.indexOf(option.value) >= 0
          )
        : options.find(
            (option: { value: any }) =>
              JSON.stringify(option.value) === JSON.stringify(field.value)
          );
    } else {
      return isMulti ? [] : '';
    }
  };

  // todo: make this search only retailers in the country of the user?
  useEffect(() => {
    const fetchData = async () => {
      // TODO : Currently fetching all retailers for the tenant if STORES_FROM_FIRESTORE_RETAILERS is true
      if (
        !config.HOLDINGS.NEW_REGISTRATION_FLOW.STORES_FROM_FIRESTORE_RETAILERS
      ) {
        const data = await SearchRetailers(inputText, {
          hitsPerPage: 500,
          page: 0,
        });
        if (data?.items.length) {
          const newOptions: any[] = data.items.map(
            (item: DTRetailer_Public) => {
              newOptions.push({
                id: item.docid,
                label: `${item.name} (${item?.address?.town ?? ''} - ${
                  item?.address?.county ?? ''
                })`,
                value: {
                  docid: item.docid,
                  country: item?.address?.country,
                },
              });
            }
          );
          setOptions(newOptions as any);
        }
      }
      setIsLoadingResults(false);
    };

    if (disabled && value) {
      setOptions([value] as any);
      setFieldValue(name, value.id);
    } else {
      if (debouncedInput.length) {
        setIsLoadingResults(true);
        if (config.HOLDINGS.NEW_REGISTRATION_FLOW.STORES_FROM_FIRESTORE_RETAILERS) {
          getRetailerAddress();
        } else {
          void fetchData();
        }
      } else if (settings?.options && settings?.options?.length) {
        setOptions([...settings.options, ...options] as []);
      } else setOptions(options || []);
    }
  }, [debouncedInput, value]);

  useEffect(() => {
    if (config.HOLDINGS.NEW_REGISTRATION_FLOW.STORES_FROM_FIRESTORE_RETAILERS) {
      getRetailerAddress();
    }
  }, [
    config.HOLDINGS.NEW_REGISTRATION_FLOW.STORES_FROM_FIRESTORE_RETAILERS,
    activeTenantInfo,
    retailerPersonaId,
  ]);

  // TODO : Currently fetching all retailers for the tenant
  const getRetailerAddress = async () => {
    try {
      if (!settings.tenantLink) return;

      const principalsId = `${PersonaTypeSingleLetter.Retailer}${PersonaIdTypeSplitter}${settings.tenantLink}`;

      // Parallel execution of queries for better performance
      const [retailers, businessInvites] = await Promise.all([
        FBD_Retailer_Public.query()
          .where('principals', 'array-contains', principalsId)
          .getContents(),
        config.HOLDINGS.NEW_REGISTRATION_FLOW.SHOW_STORES_FROM_BUSINESS_INVITES
          ? FBD_Business_User_Invite.query()
              .where('tenantLink', '==', t('tenantLink'))
              .where(
                'personas',
                'array-contains',
                PersonaTypeSingleLetter.Retailer
              )
              .where('status', '!=', 'completed')
              .getContents()
          : Promise.resolve([]),
      ]);

      // Create base options from retailers
      const retailerOptions = retailers.map((retailer: DTRetailer_Public) => ({
        id: retailer.docid,
        label: retailer.name,
        value: { docid: retailer.docid },
      }));

      // Mapping the Retailer and BusinessUserInvite Id with name
      const retailerMap = new Map(
        retailers.map((r: DTRetailer_Public) => [r.docid, r.name])
      );

      const businessInviteMap = new Map(
        businessInvites.map((invite: DTBusiness_User_Invite) => {
          const name =
            invite.firstName && invite.lastName
              ? `${invite.firstName} ${invite.lastName}`
              : invite.orgName;
          return [invite.docid, name];
        })
      );

      const businessInviteOptions = businessInvites.map(
        (invite: DTBusiness_User_Invite) => ({
          id: `${invite.docid}`,
          label:
            invite.firstName && invite.lastName
              ? `${invite.firstName} ${invite.lastName}`
              : invite.orgName,
          value: {
            docid: `${invite.docid}`,
          },
        })
      );

      let finalOptions = [...retailerOptions, ...businessInviteOptions];

      // Handle Supagard partner case
      const isPartnerToSupagard =
        activeTenantInfo?.isPartnerTo?.includes('SUPAGARD');

      if (isPartnerToSupagard) {
        const mainGroup: any = await nestApiGetMainGroup({
          docid: activeTenantInfo?.info?.mainGroup || '',
        });
        if (
          mainGroup?.associatedPartners?.length > 0 &&
          mainGroup?.sharedLocation
        ) {
          finalOptions = mainGroup.associatedPartners.map((item: any) => {
            const key = item.tenantLink
              ? `${PersonaTypeSingleLetter.Retailer}:${item.tenantLink}`
              : item.businessUserInviteLink;
            const label =
              item.tenantLink && retailerMap.has(item.tenantLink)
                ? retailerMap.get(item.tenantLink)
                : item.businessUserInviteLink &&
                  businessInviteMap.has(item.businessUserInviteLink)
                ? businessInviteMap.get(item.businessUserInviteLink)
                : item.name;

            return {
              id: key,
              label,
              value: { docid: key },
            };
          });
        } else {
          finalOptions = [
            {
              id: retailerPersonaId || '',
              label: activeTenantInfo?.orgName || '',
              value: { docid: retailerPersonaId || '' },
            },
          ];
        }
      }
      setOptions(finalOptions as any);
      config.HOLDINGS.NEW_REGISTRATION_FLOW.DEFAULT_STORE &&
        setFieldValue(name, retailerPersonaId);
    } catch (error) {
      console.error('Error in getRetailerAddress:', error);
      // Handle error appropriately - you might want to set an error state or show a notification
    }
  };

  const onInputChange = (e: string) => {
    setInputText(e);
  };

  return (
    <Select
      {...field}
      {...props}
      options={options}
      isMulti={isMulti}
      classNames={generateSelectStyles(errorField)}
      //styles={errorField ? errorCustomStyles : customStyles}
      placeholder={placeholder}
      defaultValue={field.value}
      components={
        settings.dropDown === 'disabled'
          ? {
              DropdownIndicator: () => null,
              IndicatorSeparator: () => null,
            }
          : undefined
      }
      value={
        options
          ? isMulti
            ? options.find(
                (option: { value: any }) => option.value === field.value
              )
            : getValue()
          : ''
      }
      // Doesn't seem to be doing anything, uncomment if necessary - dc
      // error
      onKeyDown={handleKeyPress}
      onChange={defaultOnChange ?? onChangeDefault}
      onMenuOpen={() => Bus.emit(Bus.events.modalContent, true)}
      onMenuClose={() => Bus.emit(Bus.events.modalContent, false)}
      onInputChange={onInputChange}
      isClearable={isClearable}
      isDisabled={disabled}
      noOptionsMessage={({ inputValue }) =>
        !inputValue
          ? 'Type something...'
          : inputText === debouncedInput && !isLoadingResults
          ? 'No results found'
          : 'Loading...'
      }
    />
  );
}

export default InputAutoCompleteLocationRetailers;
